import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ProductoEmpresa } from '@/components/hooks/useEmpresa';
import { Sparkles, Tag, X } from 'lucide-react';
import { motion } from 'framer-motion';

interface ProductDialogProps {
  producto: ProductoEmpresa | null;
  onClose: () => void;
}

const SHEET_CLASS = `
  z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
  [&>button:last-child]:hidden
  flex flex-col
  !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
  w-full max-w-none max-h-[90vh] rounded-t-2xl rounded-b-none
  data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
  sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
  sm:w-[96vw] sm:max-w-[1400px] sm:mx-auto sm:h-auto sm:max-h-[80vh] sm:rounded-3xl sm:shadow-2xl
`;

const ProductDialog = ({ producto, onClose }: ProductDialogProps) => {
  if (!producto) return null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className={SHEET_CLASS}>
        {/* Botón cerrar */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 dark:bg-white/10 text-gray-500 dark:text-gray-300 hover:bg-white dark:hover:bg-white/20 transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col sm:flex-row flex-1 overflow-y-auto sm:overflow-hidden">
          {/* ── Zona imagen ── */}
          <div className="sm:w-[420px] shrink-0 p-5 flex items-center justify-center bg-white dark:bg-white/5 h-64 sm:h-full">
            <div className="relative w-full rounded-2xl overflow-hidden h-full">
              {producto.imagen_principal ? (
                <motion.img
                  src={producto.imagen_principal}
                  alt={producto.nombre}
                  initial={{ scale: 1.06, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.55, ease: 'easeOut' }}
                  className="w-full h-full object-contain p-4"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Sparkles className="w-12 h-12 text-gray-200 dark:text-white/20" />
                </div>
              )}

              <div className="absolute top-3 left-3">
                <span className="flex items-center gap-1.5 text-[9px] font-bold tracking-[0.3em] uppercase bg-[#e63950]/10 text-[#e63950] px-3 py-1.5 rounded-full backdrop-blur-sm">
                  <Tag className="w-2.5 h-2.5" />
                  Producto
                </span>
              </div>
            </div>
          </div>

          {/* ── Contenido ── */}
          <div className="flex-1 px-5 sm:px-7 py-5 sm:py-6 overflow-y-auto flex flex-col">
            {producto.precio !== null && (
              <div className="mb-3">
                <span className="inline-block bg-[#fdecea] dark:bg-[#e63950]/15 text-[#e63950] text-sm font-bold px-3 py-1 rounded-full tabular-nums">
                  {producto.precio.toFixed(0)}€
                </span>
              </div>
            )}

            <DialogHeader className="mb-0 space-y-0">
              <DialogTitle className="text-xl sm:text-3xl font-light text-[#0f172a] dark:text-white tracking-wide leading-snug pr-8">
                {producto.nombre}
              </DialogTitle>
            </DialogHeader>

            <div className="w-10 h-px bg-[#e63950] mt-4 mb-5" />

            {producto.descripcion ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 font-light leading-relaxed whitespace-pre-line flex-1">
                {producto.descripcion}
              </p>
            ) : (
              <div className="flex-1" />
            )}

            {producto.precio !== null && (
              <div className="mt-6 pt-5 border-t border-gray-100 dark:border-white/10 flex items-center justify-between">
                <p className="text-2xl sm:text-3xl font-light text-[#0f172a] dark:text-white tabular-nums">
                  {producto.precio.toFixed(0)}
                  <span className="text-lg ml-1 text-gray-400">€</span>
                </p>
                <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-gray-400">
                  IVA incluido
                </span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDialog;